import { useState } from "react";
import {
  Sparkles,
  MapPin,
  Phone,
  User,
  Briefcase,
  Calendar,
  Clock,
  Share2,
  Heart,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TuviLaSoBoard } from "@/components/tuvi/tuvi-la-so-board";
import { BaziBoard } from "@/components/bazi/bazi-board";
import type { FullMatchProfile, ShareableField } from "@/lib/boide-api";

/** Avatar bấm vào để xem ảnh phóng to (lightbox). */
export function ViewableAvatar({
  src,
  alias,
  className,
  fallbackClassName,
}: {
  src?: string | null;
  alias?: string | null;
  className?: string;
  fallbackClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const label = alias || "Ẩn danh";
  return (
    <>
      <button
        type="button"
        onClick={() => src && setOpen(true)}
        className={src ? "cursor-zoom-in" : "cursor-default"}
        aria-label={src ? "Xem ảnh" : undefined}
      >
        <Avatar className={className}>
          {src ? <AvatarImage src={src} alt={label} /> : null}
          <AvatarFallback className={fallbackClassName}>
            {label.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </button>
      {src ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md border-gold-soft/40 bg-background/95 p-3">
            <DialogHeader>
              <DialogTitle className="text-center text-sm text-muted-foreground">
                {label}
              </DialogTitle>
            </DialogHeader>
            <img
              src={src}
              alt={label}
              className="mx-auto max-h-[70vh] w-full rounded-xl object-contain"
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

export function bandColor(key?: string): string {
  switch (key) {
    case "troi_sinh":
      return "text-pink-400 border-pink-400/50 bg-pink-400/10";
    case "rat_hop":
      return "text-emerald-400 border-emerald-400/50 bg-emerald-400/10";
    case "hop":
      return "text-sky-400 border-sky-400/50 bg-sky-400/10";
    case "binh_thuong":
      return "text-amber-400 border-amber-400/50 bg-amber-400/10";
    default:
      return "text-rose-400 border-rose-400/50 bg-rose-400/10";
  }
}

export function SocialLinksValue({ value }: { value: Record<string, string> }) {
  const SOCIAL_LABELS: Record<string, string> = {
    facebook: "Facebook",
    zalo: "Zalo",
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
  };
  const entries = Object.entries(value || {}).filter(([, v]) => v && String(v).trim());
  if (entries.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-col items-end gap-0.5">
      {entries.map(([k, v]) => {
        const isUrl = /^https?:\/\//i.test(v);
        return isUrl ? (
          <a
            key={k}
            href={v}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:brightness-110"
          >
            {SOCIAL_LABELS[k] || k}
          </a>
        ) : (
          <span key={k}>
            {SOCIAL_LABELS[k] || k}: {v}
          </span>
        );
      })}
    </div>
  );
}

const GOAL_TAG_LABELS: Record<string, string> = {
  // se_duyen
  "gia_dinh": "Gia đình",
  "con_cai": "Con cái",
  "su_nghiep": "Sự nghiệp",
  "on_dinh": "Ổn định",
  "tu_do": "Tự do, độc lập",
  "phieu_luu": "Phiêu lưu, trải nghiệm",
  "tam_linh": "Tâm linh",
  "suc_khoe": "Sức khỏe, thể thao",
  "hoc_hoi": "Học hỏi, phát triển bản thân",
  "du_lich": "Du lịch",
  "tai_chinh": "Tài chính vững vàng",
  "cong_dong": "Cống hiến cộng đồng",
  // lam_an
  "tang_truong": "Tăng trưởng nhanh",
  "ben_vung": "Bền vững, dài hạn",
  "doi_moi": "Đổi mới, sáng tạo",
  "an_toan": "An toàn, ít rủi ro",
  "mo_rong": "Mở rộng quy mô",
  "loi_nhuan": "Tối đa lợi nhuận",
  "uy_tin": "Uy tín thương hiệu",
  "cong_nghe": "Công nghệ",
  "tac_dong_xa_hoi": "Tác động xã hội",
  "toan_cau": "Vươn tầm quốc tế",
};

/** Thân hồ sơ đầy đủ — dùng chung cho trang xem hồ sơ (click từ feed) VÀ chi tiết kết nối. */
export function FullProfileBody({
  profile,
  allowReveal = false,
}: {
  profile: FullMatchProfile;
  allowReveal?: boolean;
}) {
  const alias = profile.display_alias || "Ẩn danh";
  const photos = [profile.avatar, ...(profile.photos ?? [])].filter(Boolean) as string[];
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [chartTab, setChartTab] = useState<"tom-tat" | "day-du">("tom-tat");
  const hasChart = !!(profile.tuvi_chart || profile.bazi_chart);

  const getDetailIcon = (label: string) => {
    switch (label) {
      case "Họ tên": return <User className="h-4 w-4 text-primary/80" />;
      case "Nghề nghiệp": return <Briefcase className="h-4 w-4 text-primary/80" />;
      case "Nơi ở": return <MapPin className="h-4 w-4 text-primary/80" />;
      case "Sở thích": return <Heart className="h-4 w-4 text-primary/80" />;
      case "Ngày sinh": return <Calendar className="h-4 w-4 text-primary/80" />;
      case "Giờ sinh": return <Clock className="h-4 w-4 text-primary/80" />;
      case "SĐT": return <Phone className="h-4 w-4 text-primary/80" />;
      case "Mạng xã hội": return <Share2 className="h-4 w-4 text-primary/80" />;
      default: return null;
    }
  };

  const [revealedFields, setRevealedFields] = useState<Record<string, boolean>>({});

  const renderSensitiveValue = (
    label: string,
    value: React.ReactNode,
    rawValue?: string | null,
    fieldKey?: ShareableField
  ) => {
    // Ẩn theo thiết lập người dùng (birth_time, email, phone mặc định ẩn; các trường khác mặc định hiện)
    const visibility = profile.field_visibility || {};
    const isDefaultHidden = fieldKey ? ["birth_time", "email", "phone"].includes(fieldKey) : false;
    const isHiddenByUser = fieldKey
      ? (visibility[fieldKey] === undefined ? isDefaultHidden : visibility[fieldKey] === false)
      : false;

    if (!isHiddenByUser) return value;

    const isRevealed = revealedFields[label];
    if (isRevealed) return value;

    let maskedText = "••••••••";
    const strRaw = rawValue || "";
    if (label === "SĐT" && strRaw) {
      maskedText = strRaw.replace(/\d(?=\d{3})/g, "•"); // VD: •••••••567
    } else if (label === "Họ tên" && strRaw) {
      const parts = strRaw.trim().split(" ");
      if (parts.length > 1) {
        maskedText = parts[0] + " " + parts.slice(1).map(() => "•••").join(" ");
      } else {
        maskedText = "•••";
      }
    } else if (label === "Ngày sinh") {
      maskedText = "••/••/••••";
    } else if (label === "Giờ sinh") {
      maskedText = "••:••";
    } else if (label === "Mạng xã hội") {
      maskedText = "Liên kết ẩn";
    }

    if (!allowReveal) {
      return <span className="text-muted-foreground/60 select-none font-medium">{maskedText}</span>;
    }

    return (
      <button
        type="button"
        onClick={() => setRevealedFields((prev) => ({ ...prev, [label]: true }))}
        className="inline-flex items-center gap-1 text-xs text-amber-500 hover:text-amber-400 bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/25 px-2 py-0.5 rounded-full transition-all cursor-pointer select-none"
        title="Nhấp để hiển thị thông tin"
      >
        <Eye className="h-3 w-3" />
        <span>{maskedText}</span>
      </button>
    );
  };

  const details: {
    label: string;
    value: React.ReactNode;
    rawValue?: string | null;
    fieldKey?: ShareableField;
  }[] = [];

  if (profile.real_name)
    details.push({
      label: "Họ tên",
      value: profile.real_name,
      rawValue: profile.real_name,
      fieldKey: "real_name",
    });
  if (profile.occupation)
    details.push({
      label: "Nghề nghiệp",
      value: profile.occupation,
      fieldKey: "occupation",
    });
  if (profile.current_location)
    details.push({
      label: "Nơi ở",
      value: profile.current_location,
      fieldKey: "current_location",
    });
  if (profile.hobbies && profile.hobbies.length > 0)
    details.push({
      label: "Sở thích",
      value: profile.hobbies.join(", "),
      fieldKey: "hobbies",
    });
  if (profile.birth_date)
    details.push({
      label: "Ngày sinh",
      value: profile.birth_date,
      rawValue: profile.birth_date,
      fieldKey: "birth_date",
    });
  if (profile.birth_time)
    details.push({
      label: "Giờ sinh",
      value: profile.birth_time,
      rawValue: profile.birth_time,
      fieldKey: "birth_time",
    });
  if (profile.phone)
    details.push({
      label: "SĐT",
      value: (
        <a href={`tel:${profile.phone}`} className="flex items-center gap-1 text-primary">
          {profile.phone}
        </a>
      ),
      rawValue: profile.phone,
      fieldKey: "phone",
    });
  if (profile.social_links && Object.keys(profile.social_links).length > 0)
    details.push({
      label: "Mạng xã hội",
      value: <SocialLinksValue value={profile.social_links} />,
      rawValue: "social",
      fieldKey: "social_links",
    });

  return (
    <div className="space-y-5 w-full min-w-0 overflow-hidden">
      {/* Album ảnh trình chiếu */}
      {photos.length > 0 ? (
        <div className="space-y-3">
          <div className="relative mx-auto aspect-[3/4] w-full max-w-xs overflow-hidden rounded-2xl border border-gold-soft/30 shadow-mystic bg-background/50">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="absolute inset-0 h-full w-full cursor-zoom-in"
              title="Click để phóng to"
            >
              <img
                src={photos[activePhotoIdx]}
                alt={`${alias} - Ảnh ${activePhotoIdx + 1}`}
                className="h-full w-full object-cover transition-all duration-300 hover:scale-105"
              />
            </button>

            {/* Phím điều hướng ảnh */}
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhotoIdx((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
                  }}
                  className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/85"
                  aria-label="Ảnh trước"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhotoIdx((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
                  }}
                  className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/85"
                  aria-label="Ảnh sau"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}

            {/* Các vạch chỉ số lượng ảnh */}
            {photos.length > 1 && (
              <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
                {photos.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePhotoIdx(idx);
                    }}
                    className={`h-1.5 w-1.5 rounded-full transition-all ${
                      idx === activePhotoIdx ? "bg-white w-3" : "bg-white/50"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Danh sách ảnh thu nhỏ phía dưới */}
          {photos.length > 1 && (
            <div className="flex justify-center gap-2 overflow-x-auto py-1 max-w-xs mx-auto">
              {photos.map((src, i) => (
                <button
                  key={src + i}
                  type="button"
                  onClick={() => setActivePhotoIdx(i)}
                  className={`h-14 w-11 shrink-0 overflow-hidden rounded-lg border transition ${
                    i === activePhotoIdx
                      ? "border-primary scale-105 shadow-md"
                      : "border-gold-soft/20 opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt="thumbnail" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Hộp thoại Dialog Zoom ảnh */}
          <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
            <DialogContent className="max-w-md border-gold-soft/40 bg-background/95 p-3">
              <DialogHeader>
                <DialogTitle className="text-center text-sm text-muted-foreground">
                  {alias} ({activePhotoIdx + 1}/{photos.length})
                </DialogTitle>
              </DialogHeader>
              <div className="relative flex items-center justify-center min-h-[300px]">
                <img
                  src={photos[activePhotoIdx]}
                  alt={alias}
                  className="mx-auto max-h-[70vh] w-full rounded-xl object-contain"
                />
                {photos.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setActivePhotoIdx((prev) => (prev > 0 ? prev - 1 : photos.length - 1))}
                      className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivePhotoIdx((prev) => (prev < photos.length - 1 ? prev + 1 : 0))}
                      className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <div className="mx-auto w-fit">
          <ViewableAvatar
            src={profile.avatar}
            alias={alias}
            className="h-20 w-20 border-2 border-gold-soft/50"
            fallbackClassName="bg-gold-gradient text-xl text-primary-foreground"
          />
        </div>
      )}

      {/* Thông tin cơ bản */}
      <div className="text-center">
        <div className="text-xl font-bold text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {alias}
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
          {profile.chi_nam ? <span className="bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full text-foreground/80 font-medium">Tuổi {profile.chi_nam}</span> : null}
          {profile.age ? <span>· {profile.age} tuổi</span> : null}
          {profile.gender ? (
            <span>
              · {profile.gender === "male" ? "Nam" : profile.gender === "female" ? "Nữ" : "Khác"}
            </span>
          ) : null}
        </div>
        {profile.province || profile.distance_label ? (
          <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 text-primary/80" />
            {profile.province ? <span>{profile.province}</span> : null}
            {profile.distance_label ? (
              <span className="font-semibold text-primary/90 bg-gold-gradient/10 border border-[#E5B869]/20 px-2 py-0.5 rounded-full">
                {profile.distance_label}
              </span>
            ) : null}
          </div>
        ) : null}
        
        {/* Điểm tương hợp */}
        <div
          className={`mx-auto mt-4 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold transition-all duration-300 shadow-sm ${bandColor(
            profile.band?.key,
          )}`}
        >
          <Sparkles className="h-4 w-4 animate-pulse" />
          Hợp {profile.prelim_score}/100 · {profile.band?.label}
        </div>
        {profile.match_reasons && profile.match_reasons.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
            {profile.match_reasons.map((r, i) => (
              <span
                key={i}
                className="rounded-full border border-primary/20 bg-primary/5 px-2.5 py-1 text-[11px] text-foreground/90 shadow-2xs"
              >
                {r}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {profile.goal_tags && profile.goal_tags.length > 0 ? (
        <div className="flex flex-wrap justify-center gap-1.5 pt-1">
          {profile.goal_tags.map((t) => (
            <Badge key={t} variant="outline" className="text-[11px] border-gold-soft/30 bg-card/40 text-muted-foreground font-normal px-2.5 py-0.5">
              {GOAL_TAG_LABELS[t] || t}
            </Badge>
          ))}
        </div>
      ) : null}

      {details.length > 0 ? (
        <div className="space-y-3">
          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2 pt-2">
            {details.map((d, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-3 rounded-xl border border-border/30 bg-background/25 px-4 py-2.5 shadow-2xs transition hover:border-gold-soft/20"
              >
                <dt className="flex items-center gap-2 text-muted-foreground shrink-0">
                  {getDetailIcon(d.label)}
                  <span className="text-[13px]">{d.label}</span>
                </dt>
                <dd className="text-right font-medium text-foreground text-[13px] max-w-[180px] sm:max-w-[240px] break-words">{renderSensitiveValue(d.label, d.value, d.rawValue, d.fieldKey)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <p className="text-center text-xs text-muted-foreground py-4">
          Người này chưa công khai thêm thông tin nào.
        </p>
      )}

      {/* Bản đồ lá số */}
      {hasChart ? (
        <div className="pt-2">
          <div className="mb-3 flex justify-center gap-1.5">
            {(["tom-tat", "day-du"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setChartTab(t)}
                className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-all ${
                  chartTab === t
                    ? "border-primary/60 bg-gold-gradient text-primary-foreground shadow-sm"
                    : "border-border/40 bg-background/40 text-muted-foreground hover:text-foreground"
                }`}
              >
                {t === "tom-tat" ? "Tóm tắt lá số" : "Lá số đầy đủ"}
              </button>
            ))}
          </div>
          {chartTab === "tom-tat" ? (
            <div className="space-y-2 rounded-xl border border-gold-soft/30 bg-background/30 p-4 text-sm shadow-2xs">
              {profile.tuvi_chart ? (
                <div className="flex items-center justify-between border-b border-border/20 pb-2">
                  <span className="text-muted-foreground">Tử Vi — Bản mệnh:</span>
                  <span className="font-semibold text-foreground bg-primary/5 px-2.5 py-0.5 rounded-md border border-primary/20">{(profile.tuvi_chart as { ban_menh?: string }).ban_menh}</span>
                </div>
              ) : null}
              {profile.bazi_chart ? (
                <div className="flex items-center justify-between pt-1">
                  <span className="text-muted-foreground">Bát Tự — Nhật chủ:</span>
                  <span className="font-semibold text-foreground bg-primary/5 px-2.5 py-0.5 rounded-md border border-primary/20">
                    {
                      (profile.bazi_chart as { day_master?: { day_master?: string } }).day_master
                        ?.day_master
                    }
                  </span>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4 overflow-x-auto rounded-xl border border-gold-soft/20 bg-background/20 p-2 w-full max-w-full min-w-0">
              {profile.tuvi_chart ? <TuviLaSoBoard apiChart={profile.tuvi_chart} maskPii /> : null}
              {profile.bazi_chart ? <BaziBoard chart={profile.bazi_chart} maskPii /> : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
